import React from 'react';

import classNames from 'classnames';

import { typeChipLabel } from './queueTypes';

/**
 * The type filter for a unified list, driven entirely off `counts_by_type`.
 *
 * TWO RULES:
 *
 *  - A type the payload did not count has NO chip. The server omits a type with
 *    nothing in it rather than sending `0`, so there is no chip a user can press
 *    to reach an empty table — which is the whole failure of the per-type tab
 *    layout this replaces.
 *  - Fewer than two types means NO CHIP ROW AT ALL. A user entitled to raise one
 *    kind of request sees the single list and nothing to filter it with.
 *
 * The badge inverts on the selected chip, the same way `.bc-count` inverts on an
 * active nav item — a fixed blue-on-blue-25 badge disappears against the
 * selected chip's blue fill.
 */
export default function QueueFilterChips({ countsByType = {}, selected, onSelect }) {
    const types = Object.keys(countsByType);

    if (types.length < 2) {
        return null;
    }

    /* EVERY badge, All included, comes from `countsByType` — never from the
       page's `total`, which NARROWS to the selected type. Reading `total` here
       put "All (3)" beside "Purchase requisitions (7)": a parent badge smaller
       than its own child, on the one feature whose claim is that its numbers
       agree. `countsByType` is the block the server never narrows, so summing it
       is the only figure the All chip can hold that stays true under a filter. */
    const chips = [
        { type: null, label: $t('All'), count: Object.values(countsByType).reduce((sum, count) => sum + count, 0) },
        ...types.map((type) => ({ type, label: typeChipLabel(type), count: countsByType[type] })),
    ];

    return (
        <div className="in-nav horizontal tw-mb-4" role="group" aria-label={$t('Filter by type')}>
            {chips.map((chip) => {
                const isSelected = chip.type === selected;

                return (
                    <button
                        key={chip.type ?? 'all'}
                        type="button"
                        className={classNames('in-nav__item', { active: isSelected, inactive: !isSelected })}
                        aria-pressed={isSelected}
                        onClick={() => onSelect(chip.type)}
                    >
                        {chip.label}
                        <span
                            className={classNames('tw-ms-1.5 tw-rounded-[10px] tw-px-[7px] tw-py-px tw-text-[11px] tw-font-semibold', {
                                'tw-bg-white/25 tw-text-white': isSelected,
                                'tw-bg-blue-25 tw-text-blue': !isSelected,
                            })}
                        >
                            {chip.count}
                        </span>
                    </button>
                );
            })}
        </div>
    );
}
